import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { pluck } from 'rxjs/operators';
import { Project } from '../core/objects/project';
import { NavigationService } from '../core/services/navigation.service';
import { PdfFromProjectService } from '../core/services/pdf-from-project.service';
import { ProjectApiService } from '../core/services/project-api.service';
import { ProjectService } from '../core/services/project.service';
import { SaveProjectService } from '../core/services/save-project.service';
import { UserApiService } from '../core/services/user-api.service';
import { UserService } from '../core/services/user.service';
import { ProjectInfo, SavedProject, SavedProjectBody, SharedProject } from '../core/types';
import { ShareProjectComponent } from '../top-menu/share-project/share-project.component';
import { ModalService } from '../modal/modal.service';
import { FADE_IN_ANIMATION, HEIGHT_ANIMATION } from '../shared/animations/animations';
import { WorkingTableComponent } from '../working-table/working-table.component';

import { ClickedOutsideDirective } from './../shared/directives/clicked-outside.directive';

@Component({
    selector: 'gl-mobile-menu',
    templateUrl: './mobile-menu.component.html',
    styleUrls: ['./mobile-menu.component.scss'],
    animations: [HEIGHT_ANIMATION, FADE_IN_ANIMATION],
    standalone: false
})
export class MobileMenuComponent extends ClickedOutsideDirective implements OnDestroy {
  @HostBinding('class.open') public open = false;
  @Input() public workingTable: WorkingTableComponent;
  @ViewChild('openOverlaps', { static: false, read: ElementRef })
  public openOverlapsEl: ElementRef;

  public myProjectsClicked: boolean;
  public projects: SavedProjectBody[];
  public loadsListClicked: boolean;
  public project: Project;
  public currentWorkSpaceIndex: number;
  public overlapsLoadingSpacesClicked: boolean;

  private projectInfo: ProjectInfo;
  private subscriptions: Subscription[] = [];

  constructor(
    elRef: ElementRef,
    private projectApiService: ProjectApiService,
    private projectService: ProjectService,
    private modalService: ModalService,
    private pdfFromProjectService: PdfFromProjectService,
    private saveProjectService: SaveProjectService,
    private navigationService: NavigationService,
    private userService: UserService,
    private userApiService: UserApiService,
  ) {
    super(elRef);

    this.subscriptions.push(
      this.projectService.project.subscribe((project: Project) => (this.project = project)),
    );
    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe(
        (current: number) => (this.currentWorkSpaceIndex = current),
      ),
    );

    this.subscriptions.push(
      this.projectApiService.getProjectInfo().subscribe((project: ProjectInfo) => {
        this.projectInfo = project;
      }),
    );

    this.subscriptions.push(
      this.userService.user
        .pipe(pluck('savedProjects'))
        .subscribe((savedProjects: SavedProject[]) => (this.projects = savedProjects as unknown as SavedProjectBody[])),
    );
  }

  @HostListener('clickedOutside') public onClickedOutside() {
    this.open = false;
    this.myProjectsClicked = false;
    this.loadsListClicked = false;
    this.overlapsLoadingSpacesClicked = false;
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public isWorkSpace() {
    return this.project.workSpaces.length > 0;
  }

  public openMenu() {
    this.open = !this.open;
  }

  public myProjectsClick() {
    this.myProjectsClicked = !this.myProjectsClicked;
  }

  public overlapsLoadingSpacesClick() {
    this.overlapsLoadingSpacesClicked = !this.overlapsLoadingSpacesClicked;
  }

  public openProject(project: SavedProjectBody) {
    this.userApiService.openSavedProject(project.id).subscribe();
  }

  public shareProject() {
    this.modalService.open(ShareProjectComponent);
  }

  public downloadPdf(withScreen: boolean): void {
    const projectData: SharedProject & { description: string } = {
      description: this.saveProjectService.generateProjectDescription(),
      project: this.project,
      name: this.projectInfo ? this.projectInfo.name : undefined,
      note: this.projectInfo ? this.projectInfo.note : undefined,
    };
    let projection: string = undefined;
    if (withScreen) {
      projection = this.workingTable.visualisation.getImageData(true);
    }

    this.pdfFromProjectService.generateSharePDF(projectData, undefined, {
      imageSrc: projection,
      currentWorkSpace: this.currentWorkSpaceIndex,
    }).subscribe();
  }

  public loadsListClick() {
    this.loadsListClicked = !this.loadsListClicked;
  }

  public logout() {
    localStorage.removeItem('token');
    this.navigationService.navigateToMobileLogin();
  }

  public trackByFunction(index: number) {
    return index;
  }
}
